'use client'

import { ArrowDown } from 'lucide-react'
import {
    Block,
    FeatureGrid,
    Head,
    OtherSolutions,
    SolutionCase,
    SolutionCta,
    SolutionHero,
    SolutionPage,
    VerticalFlow,
} from '@/components/platform/v2/SolutionKit'
import { getSolution } from '@/data/solutions'

const meta = getSolution('online-offline')
const online = ['Сайт', 'Приложение', 'Форма', 'Чат']
const offline = ['Телефон', 'Офис', 'Магазин', 'Менеджер']
const profile = ['CRM', 'Сделки', 'Заказы', 'Документы', 'Коммуникации', 'Задачи', 'Лояльность']

const FlowColumn = ({ title, items, action }: { title: string; items: string[]; action: string }) => (
    <div className="flex flex-col items-center rounded-3xl border border-border/60 bg-[hsl(var(--charcoal))] p-6">
        <p className="eyebrow text-primary">{title}</p>
        <div className="mt-5 grid w-full grid-cols-2 gap-2">
            {items.map((item) => (
                <div
                    key={item}
                    className="rounded-xl border border-border/50 bg-[hsl(var(--deep-black))] p-3 text-center text-sm font-bold"
                >
                    {item}
                </div>
            ))}
        </div>
        <ArrowDown className="my-4 h-5 w-5 text-primary" />
        <p className="text-center text-sm font-bold text-foreground">{action}</p>
    </div>
)

const OnlineOfflineSolution = () => (
    <SolutionPage meta={meta}>
        <SolutionHero
            meta={meta}
            eyebrow="Онлайн + офлайн клиенты"
            titleLines={['Клиенту не обязательно быть в приложении,', 'чтобы быть вашим клиентом в системе']}
            lead="VCENTRE.ONE работает одновременно с цифровой аудиторией и людьми, которые звонят, приходят лично или взаимодействуют с компанией офлайн."
            primaryLabel="Собрать решение для моего бизнеса"
            primaryTo="/signin?scenario=online-offline"
        />

        <Block tone="quiet">
            <Head
                eyebrow="Реальный бизнес работает иначе"
                title="Клиент появляется раньше регистрации"
                align="center"
                text="Он может позвонить, прийти в офис или магазин, написать менеджеру или прийти по рекомендации. Он ещё ничего не устанавливал, но бизнес уже должен с ним работать."
            />
        </Block>

        <Block>
            <Head eyebrow="Главная схема" title="Два потока сходятся в один профиль" align="center" />
            <div className="mx-auto mt-10 grid max-w-5xl gap-4 md:grid-cols-2">
                <FlowColumn title="ONLINE" items={online} action="Автоматическое создание клиента" />
                <FlowColumn title="OFFLINE" items={offline} action="Ручное создание клиента" />
            </div>
            <div className="mx-auto flex max-w-5xl flex-col items-center">
                <ArrowDown className="my-4 h-6 w-6 text-primary" />
                <div className="w-full rounded-2xl border border-primary/45 bg-primary/10 px-6 py-5 text-center text-xl font-extrabold text-primary">
                    ЕДИНЫЙ ПРОФИЛЬ VCENTRE.ONE
                </div>
                <ArrowDown className="my-4 h-6 w-6 text-primary" />
                <div className="w-full">
                    <FeatureGrid items={profile.map((title) => ({ title }))} />
                </div>
            </div>
        </Block>

        <Block tone="quiet">
            <Head eyebrow="Ключевой сценарий" title="История не начинается заново" align="center" />
            <div className="mt-10">
                <VerticalFlow
                    steps={[
                        'Сегодня клиент позвонил',
                        'Менеджер добавил его вручную',
                        'Через месяц клиент установил приложение',
                        'Аккаунт связался с существующим профилем',
                        'История продолжилась в том же профиле',
                    ]}
                />
            </div>
        </Block>

        <Block>
            <SolutionCase slugs={meta.caseSlugs} title="Проекты с онлайн- и офлайн-клиентами" />
        </Block>
        <Block tone="quiet">
            <OtherSolutions meta={meta} />
        </Block>
        <Block>
            <SolutionCta
                title="Покажите, как сейчас работает ваш бизнес"
                text="Получите концепт приложения, клиентского пути и рабочего контура."
                buttonLabel="Собрать решение"
                scenario="online-offline"
                to="/signin?scenario=online-offline&source=solution-bottom"
                visual={
                    <img
                        src={'/assets/images/platform/solutions-mascot-cta.png'}
                        alt="Утёнок VCENTRE с ракетой и ноутбуком — Запускаем быстро"
                        className="mx-auto w-full max-w-[280px] object-contain sm:max-w-[340px]"
                    />
                }
            />
        </Block>
    </SolutionPage>
)

export default OnlineOfflineSolution
